import React from 'react';
import { isEmpty } from 'lodash';
import { Button, Grid, Message } from 'semantic-ui-react';
import { Form, Input, Checkbox, Dropdown } from 'formik-semantic-ui';

import CompanyContainerSelector from '../../company/containers/selector';
import { UserRndcDivisions, UserSettingAllowedValues, UserSubscriptionStatuses } from '../constants';
import { CompanyServiceLevels } from "../../company/constants";
import {
    forcedPowerUser,
    defaultProductTypes,
    isPowerUserLocked,
    availableProductTypes,
    isCompleteWithoutLiquor,
} from '../services/rules';

const rndcDivisions = Object.values(UserRndcDivisions).map(d => ({
    key: d, text: d, value: d,
}));
const findCompany = (companies, id) => companies.find(c => c.id === id);

const UserComponentEditorForm = ({
    handleChange, setFieldValue, setFieldTouched, values, errors, companies, ...formProps
}) => {
    const selectedCompany = findCompany(companies, values.company_id);
    const isRndc = selectedCompany && selectedCompany.distributor.toLowerCase() === 'rndc';
    const noLogin = selectedCompany && ['Report', 'Invoice'].indexOf(selectedCompany.service_level) === -1;
    // these read the company for the render that is happening now, which is what we
    // want for display. inside onSelect below, values.company_id is still the previous
    // company, so the freshly looked up one has to be used there instead.
    const blockSave = isCompleteWithoutLiquor(selectedCompany);
    // power user is only the operator's to set for a Complete user outside SGWS
    const lockPowerUser = isPowerUserLocked(selectedCompany, values.service_level);

    return (
        <Grid divided>
            <Grid.Row>
                <Grid.Column width={16}>
                    <CompanyContainerSelector
                        onSelect={companyId => {
                            setFieldValue('company_id', companyId);
                            setFieldTouched('company_id');
                            const company = findCompany(companies, companyId);

                            if (!!company) {
                                setFieldValue('service_level', company.service_level);
                                setFieldValue('service_status', company.service_status);
                                setFieldValue('service_premises', company.service_premises.split(','));
                                setFieldValue('service_product_type', defaultProductTypes(
                                    company.service_level,
                                    company.service_product_type.split(','),
                                ));

                                // booleans rather than 1/0 throughout: Joi.boolean()
                                // does not convert numbers
                                if (isPowerUserLocked(company, company.service_level)) {
                                    setFieldValue(
                                        'power_user',
                                        forcedPowerUser(company, company.service_level),
                                    );
                                }
                            }
                        }}
                        error={!!errors.company_id}
                        value={values.company_id}
                    />
                </Grid.Column>
            </Grid.Row>
            {!!values.company_id && (
                <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                        <Input
                            name="name"
                            label="Name"
                            inputProps={{
                                placeholder: 'First & Last Name'
                            }}
                        />
                        <Input
                            name="email"
                            label="Email"
                            inputProps={{
                                type: "email",
                                placeholder: 'Email Address',
                            }}
                        />

                        {noLogin && (
                            <Input
                                name="password"
                                label="Password"
                                inputProps={{
                                    placeholder: 'Something secret and hard to guess',
                                }}
                            />
                        )}
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                        <Form.Group>
                            <Dropdown
                                fieldProps={{ width: 8 }}
                                inputProps={{
                                    fluid: true, multiple: true,
                                }}
                                name="service_product_type"
                                label="Services"
                                options={availableProductTypes(
                                    selectedCompany,
                                    values.service_product_type,
                                ).map(type => ({
                                    key: type, value: type, text: type
                                }))}
                            />

                            <Dropdown
                                fieldProps={{ width: 8 }}
                                inputProps={{
                                    fluid: true, multiple: true,
                                }}
                                name="service_premises"
                                label="Premises"
                                options={UserSettingAllowedValues.SERVICE_PREMISES.map(type => ({
                                    key: type, value: type, text: type
                                }))}
                            />
                        </Form.Group>

                        {isRndc && (
                            <Dropdown
                                placeholder='RNDC Division'
                                options={rndcDivisions}
                                label='RNDC Division'
                                name='rndc_division'
                            />
                        )}

                        <Form.Group widths="equal">
                            <Dropdown
                                label="Status"
                                name="service_status"
                                options={Object.values(UserSubscriptionStatuses).map(type => ({
                                    key: type, value: type, text: type
                                }))}
                            />

                            <Dropdown
                                label="Level"
                                name="service_level"
                                inputProps={{
                                    // re-apply the liquor only default when the level is
                                    // switched to Complete, and restore the company's full
                                    // list when it is switched away from Complete
                                    onChange: (e, { value }) => {
                                        const company = findCompany(companies, values.company_id);

                                        if (!!company) {
                                            setFieldValue('service_product_type', defaultProductTypes(
                                                value,
                                                company.service_product_type.split(','),
                                            ));

                                            // follow the level: dropping below Complete
                                            // clears the flag for any company, and SGWS
                                            // Complete users get it back
                                            if (isPowerUserLocked(company, value)) {
                                                setFieldValue(
                                                    'power_user',
                                                    forcedPowerUser(company, value),
                                                );
                                            }
                                        }
                                    },
                                }}
                                options={Object.values(CompanyServiceLevels).map(type => ({
                                    key: type, value: type, text: type
                                }))}
                            />

                            <Form.Field>
                                <label>Power User</label>
                                <Checkbox
                                    inputProps={{ toggle: true, disabled: lockPowerUser }}
                                    value={(values.power_user > 0)}
                                    name="power_user"
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Force Password Change</label>
                                <Checkbox
                                    inputProps={{ toggle: true }}
                                    value={(values.force_change_password > 0)}
                                    name="force_change_password"
                                />
                            </Form.Field>
                        </Form.Group>

                    </Grid.Column>
                    <Grid.Column width={16}>
                        {blockSave && (
                            <Message
                                negative
                                icon="warning sign"
                                header="This company has no Liquor access"
                                content={`${selectedCompany.company} is set to Complete but its product types are "${selectedCompany.service_product_type}". Users cannot be created for it until the company record is corrected.`}
                            />
                        )}
                        <br />
                        <Button
                            disabled={isEmpty(formProps.touched) || blockSave}
                            color="teal"
                            type="submit"
                        >
                            Save User
                        </Button>
                    </Grid.Column>
                </Grid.Row>
            )}
        </Grid>
    )
};

export default UserComponentEditorForm;